import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { KeyRound, Copy, Check, ShieldAlert, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

type AccountingUser = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  role: string;
  created_at: string;
};

export default function AccountingCredentials() {
  const [confirm, setConfirm] = useState<AccountingUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ email: string; password: string; rotated_at: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accounting-users'],
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('user_id, role, created_at')
        .in('role', ['accounting_manager', 'accounting_staff']);
      if (error) throw error;
      const ids = [...new Set((roles || []).map((r: any) => r.user_id))];
      if (!ids.length) return [] as AccountingUser[];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, phone')
        .in('user_id', ids);
      const pMap = new Map<string, any>();
      (profiles || []).forEach((p: any) => pMap.set(p.user_id, p));
      return (roles as any[]).map((r) => {
        const p = pMap.get(r.user_id) || {};
        return {
          user_id: r.user_id,
          role: r.role,
          created_at: r.created_at,
          full_name: p.full_name || null,
          email: p.email || null,
          phone: p.phone || null,
        } as AccountingUser;
      }).sort((a, b) => (a.role.localeCompare(b.role)) || (a.full_name || '').localeCompare(b.full_name || ''));
    },
  });

  const rotate = async () => {
    if (!confirm) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke('rotate-staff-password', {
      body: { user_id: confirm.user_id },
    });
    setBusy(false);
    if (error || (data as any)?.error) {
      toast.error((error?.message || (data as any)?.error) || 'Failed to rotate password');
      return;
    }
    setResult({
      email: (data as any).email,
      password: (data as any).password,
      rotated_at: (data as any).rotated_at,
    });
    setConfirm(null);
    toast.success('Password rotated. Share new credentials securely.');
  };

  const copyCreds = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Email: ${result.email}\nPassword: ${result.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-primary" /> Accounting Credentials
        </h1>
        <p className="text-sm text-muted-foreground">
          Rotate and reset passwords for Accounting Manager and Accounting Staff. A new strong password is generated each time.
        </p>
      </div>

      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-4 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 dark:text-amber-200">
            Rotating a password immediately invalidates the user's previous password and signs them out of new sessions.
            The new password is shown <strong>only once</strong> — copy and share it through a secure channel.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Accounting users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-48 m-4" />
          ) : !data?.length ? (
            <p className="text-sm text-muted-foreground text-center py-12">No accounting users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((u) => (
                    <TableRow key={`${u.user_id}-${u.role}`}>
                      <TableCell className="text-xs font-medium">{u.full_name || '—'}</TableCell>
                      <TableCell className="text-xs">{u.email || '—'}</TableCell>
                      <TableCell className="text-xs">{u.phone || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {u.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => setConfirm(u)}>
                          <RefreshCw className="h-3.5 w-3.5" /> Rotate password
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate password?</AlertDialogTitle>
            <AlertDialogDescription>
              A new strong password will be generated for{' '}
              <strong>{confirm?.full_name || confirm?.email}</strong>. Their existing password will stop
              working immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={rotate} disabled={busy}>
              {busy ? 'Rotating…' : 'Yes, rotate password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Result */}
      <Dialog open={!!result} onOpenChange={(o) => !o && (setResult(null), refetch())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> New credentials
            </DialogTitle>
            <DialogDescription>
              Copy and share securely. This password will not be shown again.
              {result && <span className="block mt-1 text-[11px] text-muted-foreground">Rotated {format(new Date(result.rotated_at), 'd MMM yyyy, HH:mm')}</span>}
            </DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Email</label>
                <Input readOnly value={result.email} className="font-mono text-xs" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">New password</label>
                <Input readOnly value={result.password} className="font-mono text-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={copyCreds} className="gap-1">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy email & password'}
            </Button>
            <Button onClick={() => { setResult(null); refetch(); }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
